export interface PinterestPixelItem {
  id: string | number
  quantity?: number
  /** Minor currency units (e.g. cents), matching Vendure's Money type. */
  price?: number
}

export interface PinterestPixelCustomerData {
  email?: string
  phone?: string
}

// `orderTotal` (minor units) should be the order's actual charged total —
// summing per-line list prices ignores coupons/promotions and would over-report
// `value` to the ad account whenever a discount is active. Item-level prices in
// `contents[]` intentionally stay list price, for catalog matching.
function itemsCustomData(items: PinterestPixelItem[], currency?: string, orderTotal?: number) {
  const summedValue = items.reduce((sum, item) => sum + ((item.price ?? 0) / 100) * (item.quantity ?? 1), 0)
  const value = orderTotal != null ? orderTotal / 100 : summedValue

  return {
    currency,
    value: Math.round(value * 100) / 100,
    content_ids: items.map(item => String(item.id)),
    contents: items.map(item => ({
      id: String(item.id),
      quantity: item.quantity ?? 1,
      item_price: item.price != null ? item.price / 100 : undefined,
    })),
    num_items: items.reduce((sum, item) => sum + (item.quantity ?? 1), 0),
  }
}

export function usePinterestPixel() {
  // `pinterestPixel` is only declared in apps that opt in (see apps/exalted.com/app/config/runtime.ts),
  // so it's untyped for every other app extending this layer — cast like the Meta Pixel composable.
  const config = useRuntimeConfig() as any
  const enabled = computed(() => !!config.public?.pinterestPixelId)

  function track(eventName: string, customData?: Record<string, unknown>, userData?: PinterestPixelCustomerData, dedupeId?: string) {
    if (!enabled.value)
      return

    // Terminal, order-level events (checkout) pass a deterministic id derived from
    // the order code so a confirmation-page reload or poll re-run dedupes against
    // the first send — both browser↔CAPI and across page loads. Repeatable events
    // (page_visit/add_to_cart) get a fresh id so legitimate repeats aren't dropped.
    const eventId = dedupeId ?? crypto.randomUUID()

    window.pintrk?.('track', eventName, { ...customData, event_id: eventId })

    $fetch('/api/pinterest-events', {
      method: 'POST',
      // Survive a navigation away mid-request — without this the browser
      // cancels the send and the event is silently lost.
      keepalive: true,
      body: {
        eventName,
        eventId,
        eventSourceUrl: window.location.href,
        customData,
        userData,
      },
    }).catch(() => { /* best-effort telemetry, never surface to the UI */ })
  }

  return {
    enabled,
    track,
    // Pinterest's taxonomy has no distinct "view content" event — `page_visit`
    // carrying product data is the closest standard equivalent.
    viewContent: (item: PinterestPixelItem, currency?: string) =>
      track('page_visit', itemsCustomData([item], currency)),
    addToCart: (item: PinterestPixelItem, currency?: string) =>
      track('add_to_cart', itemsCustomData([item], currency)),
    // Pinterest's standard event for a completed order is "checkout" — there's
    // no separate InitiateCheckout/AddPaymentInfo in its taxonomy.
    purchase: (items: PinterestPixelItem[], currency?: string, orderTotal?: number, orderId?: string, userData?: PinterestPixelCustomerData) =>
      track('checkout', { ...itemsCustomData(items, currency, orderTotal), order_id: orderId }, userData, orderId ? `checkout-${orderId}` : undefined),
  }
}
